'use client';

import { InputHTMLAttributes, useState } from 'react';
import ErrorCircle from '@fluentui/svg-icons/icons/error_circle_20_filled.svg';
import EyeShow from '@fluentui/svg-icons/icons/eye_16_regular.svg';
import EyeHide from '@fluentui/svg-icons/icons/eye_off_16_regular.svg';
import { Tooltip, TooltipContent, TooltipTrigger } from '../shadcn/tooltip';
import TranslatedText from './TranslatedText';

interface Props extends InputHTMLAttributes<HTMLInputElement> {
  name: string;
  label?: string;
  value?: string | number;
  onChange?: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  messages?: string | string[];
  trailing?: string;
  error?: boolean;
}

function PasswordField({
  name,
  label,
  value,
  onChange,
  messages,
  trailing,
  error,
  ...otherProps
}: Props) {
  const [show, setShow] = useState(false);

  const containerClasses = `
    relative
    h-10
    w-full
    ${otherProps.className || ''}
  `.trim();

  const inputBaseClasses = `
    absolute inset-0 w-full rounded-lg border-none outline-none p-4
    ${error ? 'ring-[0.7px] ring-red-700 hover:ring-red-700 focus:ring-red-700' : 'hover:ring-gray-200'}
    text-gray-950 text-sm font-normal
    body-large peer
    transition-colors duration-200 ring-[0.7px] ring-gray-300 hover:ring-[0.7px] hover:border-none focus:ring-blue-300 focus:border-blue-300 focus:ring-2 focus:transition-all focus:duration-200
  `;

  const inputClasses = `${inputBaseClasses}`.trim();

  // Define reusable class name constants
  const peerFocusClasses = `
    peer-focus:top-2 rtl:peer-focus:right-[13px] ltr:peer-focus:left-[13px]
    peer-focus:text-xs peer-focus:font-medium peer-focus:px-1 peer-focus:gap-0.5
  `.trim();

  const peerAutoFillClasses = `
    peer-autofill:top-2 rtl:peer-autofill:right-[13px] ltr:peer-autofill:left-[13px]
    peer-autofill:text-xs peer-autofill:font-medium peer-autofill:px-1 peer-autofill:gap-0.5
  `.trim();

  const peerNotPlaceholderShownClasses = `
    peer-[:not(:placeholder-shown)]:top-2 rtl:peer-[:not(:placeholder-shown)]:right-[13px] ltr:peer-[:not(:placeholder-shown)]:left-[13px]
    peer-[:not(:placeholder-shown)]:text-xs peer-[:not(:placeholder-shown)]:font-medium
    peer-[:not(:placeholder-shown)]:px-1 peer-[:not(:placeholder-shown)]:gap-0.5
  `.trim();

  // Update labelClasses
  const labelClasses = `
    absolute rtl:right-4 ltr:left-4 top-4 flex gap-1 rounded-[0.625rem] text-on-surface-low pointer-events-none bg-[transparent]
    motion-safe:transition-[top,right,left,font-size,font-weight,background-color_50ms] motion-safe:duration-200
    motion-safe:ease-in-out
    ${peerFocusClasses}
    ${peerAutoFillClasses}
    ${peerNotPlaceholderShownClasses}
    /* When the input is empty */
    peer-placeholder-shown:top-4 ltr:peer-placeholder-shown:right-4 rtl:peer-placeholder-shown:left-4
  `.trim();

  return (
    <span className="flex w-full flex-col">
      <div className={containerClasses}>
        <input
          id={name}
          name={name}
          className={inputClasses}
          placeholder=" "
          type={show ? 'text' : 'password'}
          min={otherProps.type === 'number' ? 0 : undefined}
          lang={otherProps.type === 'number' ? 'en' : undefined}
          value={value}
          onChange={onChange}
          {...otherProps}
        />
        <div className="absolute inset-0 z-50 hidden w-full rounded-xl opacity-0 peer-disabled:block peer-disabled:bg-surface-container-highest peer-disabled:opacity-50" />
        <label htmlFor={name} className={labelClasses}>
          <p>{label}</p>
        </label>

        <div className="absolute flex h-full items-center justify-center px-4 ltr:right-0 rtl:left-0">
          <Tooltip>
            <TooltipTrigger
              type="button"
              onClick={() => {
                setShow(!show);
              }}
              className="cursor-pointer text-xs font-medium text-on-surface-medium"
            >
              {show ? (
                <EyeHide className={`${error && 'fill-red-700'}`} />
              ) : (
                <EyeShow className={`${error && 'fill-red-700'}`} />
              )}
            </TooltipTrigger>

            <TooltipContent>
              {show ? (
                <TranslatedText text="Field.hidePassword" />
              ) : (
                <TranslatedText text="Field.showPassword" />
              )}
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
      {messages && (
        <ul className="px-4 pt-1 text-xs font-normal leading-7 text-red-700">
          {(Array.isArray(messages) ? messages : [messages]).map(
            (message, index) => (
              <li key={index}>{message}</li>
            ),
          )}
        </ul>
      )}
    </span>
  );
}

export default PasswordField;
